import { useRef } from 'react';
import { CrudPage } from '../../components/CrudPage';
import type { ExcelExportConfigDto } from '../../api/models/ExcelExportConfigDto';
import { ExcelExportConfigControllerService } from '../../api/services/ExcelExportConfigControllerService';
import ExcelExportConfigFormDialog, { type ExcelExportConfigFormValues, type ExcelExportConfigFormResource } from '../forms/ExcelExportConfigFormDialog';
import { useErrorHandler } from '../../utils/errorHandler';

const toFormValues = (dto: ExcelExportConfigDto): ExcelExportConfigFormValues => ({
  id: dto.uuid ?? '',
  title: dto.title ?? '',
  sheetOrder: dto.sheetOrder ?? 1,
  resources: (dto.resources ?? []).map((resource): ExcelExportConfigFormResource => ({
    id: resource.uuid ?? `temp_${resource.resourceId}`,
    resourceId: resource.resourceId ?? '',
    resourceName: resource.resourceName ?? '',
    columnOrder: resource.columnOrder ?? 1
  }))
});

const toDto = (formValues: ExcelExportConfigFormValues): ExcelExportConfigDto => ({
  uuid: formValues.id || undefined,
  title: formValues.title?.trim() || undefined,
  sheetOrder: formValues.sheetOrder ?? 1,
  resources: (formValues.resources ?? []).map((resource) => ({
    uuid: resource.id || undefined,
    resourceId: resource.resourceId || undefined,
    resourceName: resource.resourceName || undefined,
    columnOrder: resource.columnOrder ?? 1
  }))
});

const ExcelExportConfigPage = () => {
  const { handleError } = useErrorHandler();
  const maxSheetOrderRef = useRef(0);

  const fetchData = async (): Promise<ExcelExportConfigFormValues[]> => {
    try {
      const configs = await ExcelExportConfigControllerService.getAllConfigs();
      const mapped = (configs || []).map(toFormValues);
      maxSheetOrderRef.current = mapped.reduce((max, item) => Math.max(max, item.sheetOrder ?? 0), 0);
      return mapped;
    } catch (error) {
      handleError(error, 'fetching excel export configs');
      return [];
    }
  };

  const saveData = async (data: ExcelExportConfigFormValues): Promise<ExcelExportConfigFormValues | void> => {
    try {
      const dto = toDto(data);
      const trimmedId = data.id?.trim();
      if (trimmedId) {
        const saved = await ExcelExportConfigControllerService.update(trimmedId, dto);
        return toFormValues(saved);
      }
      const saved = await ExcelExportConfigControllerService.create(dto);
      return toFormValues(saved);
    } catch (error) {
      handleError(error, 'saving excel export config');
      throw error;
    }
  };

  const handlePriorityChange = async (items: ExcelExportConfigFormValues[]) => {
    for (const item of items) {
      if (!item.id) {
        continue;
      }
      const dto = toDto(item);
      await ExcelExportConfigControllerService.update(item.id, dto);
    }
  };

  const deleteData = async (id: string): Promise<void> => {
    try {
      await ExcelExportConfigControllerService.delete(id);
    } catch (error) {
      handleError(error, 'deleting excel export config');
      throw error;
    }
  };

  return (
    <CrudPage<ExcelExportConfigFormValues>
      titleKey="excelExportConfig.title"
      gridMeta={[
        { field: 'title', headerKey: 'excelExportConfig.titleLabel', width: 260 }
      ]}
      fetchData={fetchData}
      saveData={saveData}
      deleteData={deleteData}
      FormComponent={ExcelExportConfigFormDialog}
      onAdd={() => ({
        id: '',
        title: '',
        sheetOrder: maxSheetOrderRef.current + 1,
        resources: []
      })}
      hasPriority={true}
      priorityField="sheetOrder"
      onPriorityChange={handlePriorityChange}
      entityName="excel-export-config"
      showAddButton={true}
      showEditButton={true}
      showDeleteButton={true}
      showCloneButton={true}
      allowSorting={true}
    />
  );
};

export default ExcelExportConfigPage;
